import { OnDestroy, Component, Inject } from "@angular/core";
import {
    NmsWizardStepModel,
    AbstractNmsWizardStep,
    NmsWizardStepMapData,
    NmsWizardStepValidationResponse
} from "@nms-angular-toolkit/nms-wizard";
import { ANGULARJS_TRANSLATE } from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import { Subscription } from "rxjs";
import { OscInputData, OscManagementData, OscManagementStepModel, OscPort } from "./osc-management-step.model";
import { SimpleTableColumnConfig } from "@nms-angular-toolkit/nms-simple-table";
import { EndsStepModel } from "../1-ends/ends-step.model";
import { LinksStepModel } from "../4-links/links-step.model";
import { IntermediatesStepModel } from "../3-intermediates/intermediates-step.model";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { OscManagementHttpService } from "./services/osc-management-step.service";
import { OscManagementDataService } from "./services/osc-management-data.service";
import { CircuitValidationService } from "../../../services/circuit-validation.service";
import { DwdmCircuitWizardValidationUtilsService } from "../../../services/dwdm-circuit-wizard-validation-utils.service";
import { DwdmDiagramWizardConverterService } from "../../diagram/dwdm-diagram-wizard-converter.service";
import { CircuitDiagram } from "../../../diagram/models/diagram.models";
import { TranspondersStepModel } from "../2-transponders/transponders-step.model";
import { AmplifiersStepModel } from "../5-amplifiers/amplifiers-step.model";
import { GeneralStepModel } from "../7-general/general-step.model";
import { DwdmCircuitWizardUtilsService } from "../../utils/dwdm-circuit-wizard-utils.service";

/**
 * Step OscManagement do DwdmCircuitWizard.
 */
@Component({
    selector: "osc-management-step",
    templateUrl: "./osc-management-step.component.html",
    styleUrls: ["./osc-management-step.component.scss"]
})
export class OscManagementStepComponent extends AbstractNmsWizardStep implements OnDestroy {
    model: OscManagementStepModel;
    endsModel: EndsStepModel;
    transpondersModel: TranspondersStepModel;
    intermediatesModel: IntermediatesStepModel;
    linksModel: LinksStepModel;
    amplifiersModel: AmplifiersStepModel;
    generalModel: GeneralStepModel;

    subscriptions: Array<Subscription> = [];
    useOscManagement: boolean = false;
    oscManagementColumns: SimpleTableColumnConfig[] = [];

    tableData: OscManagementData[] = [];
    private identifiersInUse: Array<string> = [];

    columnsSortable: Record<string, boolean> = {
        identifier: true,
        endName: true,
        port: true,
        linkTxName: true,
        linkRxName: true,
    };

    circuitDiagram: CircuitDiagram;
    isEdit: boolean = false;

    constructor(@Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            private readonly oscManagementHttpService: OscManagementHttpService,
            private readonly oscManagementDataService: OscManagementDataService,
            private readonly nmsDialogService: NmsDialogService,
            private readonly circuitValidationService: CircuitValidationService,
            private readonly circuitWizardValidationUtilsService: DwdmCircuitWizardValidationUtilsService,
            private readonly diagramConverterService: DwdmDiagramWizardConverterService,
            private readonly circuitWizardUtilsService: DwdmCircuitWizardUtilsService) {
        super();
        this.model = new OscManagementStepModel();
        this.model.useOscManagement = false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    goingToNextStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    goingToPreviousStep(): Promise<NmsWizardStepModel> {
        this.circuitWizardUtilsService.hideDiagram.emit(true);
        return this.resolveModel();
    }

    async comingFromNextStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        const model: Promise<NmsWizardStepModel> = this.resolveModel();
        await this.updateDiagramModel(wizardStepsCacheMap);

        return model;
    }

    async comingFromPreviousStep(inputStep: NmsWizardStepModel,
            wizardStepsCacheMap: NmsWizardStepMapData): Promise<NmsWizardStepModel> {
        this.identifiersInUse = [];

        this.endsModel = wizardStepsCacheMap["EndsStepModel"] as EndsStepModel;
        this.intermediatesModel = wizardStepsCacheMap["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = wizardStepsCacheMap["LinksStepModel"] as LinksStepModel;
        this.transpondersModel = wizardStepsCacheMap["TranspondersStepModel"] as TranspondersStepModel;
        this.amplifiersModel = wizardStepsCacheMap["AmplifiersStepModel"] as AmplifiersStepModel;
        this.generalModel = wizardStepsCacheMap["GeneralStepModel"] as GeneralStepModel;
        this.model = wizardStepsCacheMap["OscManagementStepModel"] as OscManagementStepModel || new OscManagementStepModel();

        if (this.endsModel.circuitId) {
            this.isEdit = true;
        }

        const isToggleDisabledFn = () => !this.oscManagementColumns;

        this.oscManagementColumns =  this.oscManagementDataService
                .initializeTableColumns(isToggleDisabledFn, this.emitChangesWereMade.bind(this), this.getCurrentStepModel());

        const oscInputData = this.buildOscInputData(this.endsModel, this.intermediatesModel, this.linksModel);
        const oscManagementData = await this.oscManagementHttpService.getOscPorts(oscInputData).toPromise();
        const tableData = this.oscManagementDataService
            .processOscManagementData(oscManagementData, this.linksModel.links, this.identifiersInUse);

        this.tableData = this.oscManagementDataService.fillTableDataFromCache(tableData, this.model);

        if (this.model.circuitId) {
            await this.updateDiagramModelWithCurrentValues();
            this.stepLoaded = true;

            return Promise.resolve(this.model);
        }

        this.updateDiagramModelWithCurrentValues();
        this.stepLoaded = true;

        return this.resolveModel();
    }

    async allowNext(): Promise<NmsWizardStepValidationResponse> {
        await this.resolveModel();

        if (this.model.useOscManagement) {
            const isAllOscDataNotActive = this.tableData.every((data: OscManagementData) => !data.toggleActive);
            if (isAllOscDataNotActive) {
                return Promise.reject(new NmsWizardStepValidationResponse(false,
                    this.translate.instant("dwdm.circuit.wizard.osc.management.active.without.osc.port.active")
                ));
            }

            const isSomeOscDataActiveButNotFilled = this.tableData
                .some((data: OscManagementData) => data.toggleActive && !data.port);
            if (isSomeOscDataActiveButNotFilled) {
                return Promise.reject(new NmsWizardStepValidationResponse(false,
                    this.translate.instant("dwdm.circuit.wizard.osc.management.osc.port.active.but.not.configured")
                ));
            }

            const oscsValidatorData = this.circuitWizardValidationUtilsService.buildOscsData(this.model, this.endsModel);
            return this.circuitValidationService.validateOscs(oscsValidatorData);
        }

        return Promise.resolve(new NmsWizardStepValidationResponse(true));
    }

    onUseOscManagementChange(toggleValue: boolean) {
        if (!toggleValue) {
            this.nmsDialogService.openDialog({
                title: this.translate.instant("popups.confirm.confirm"),
                description: this.translate.instant("dwdm.circuit.wizard.osc.management.confirm.disable.osc.management"),
                isConfirm: true,
                bindHtml: true
            })
            .then(() => {
                this.emitChangesWereMade(this.getCurrentStepModel());
            })
            .catch(() => {
                this.model.useOscManagement = true;
                this.emitChangesWereMade(this.getCurrentStepModel());
            });
        } else {
            this.useOscManagement = toggleValue;
            this.emitChangesWereMade(this.getCurrentStepModel());
        }
    }

    async processChanges(cache: NmsWizardStepMapData): Promise<void> {
        this.updateDiagramModel(cache);
        this.updateStepsModel(cache);
    }

    private async updateDiagramModel(cache: NmsWizardStepMapData): Promise<void> {
        await this.resolveModel();
        this.circuitDiagram = this.diagramConverterService.convert(cache, false, true);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    private async updateDiagramModelWithCurrentValues(): Promise<void> {
        await this.resolveModel();
        const stepsModel: NmsWizardStepMapData = {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.model,
            "GeneralStepModel": this.generalModel
        };
        this.circuitDiagram = this.diagramConverterService.convert(stepsModel, false, true);
        this.circuitWizardUtilsService.updateDiagram.emit(this.circuitDiagram);
    }

    /**
     * Realiza a atualização dos dados do step após um processamento de mudanças.
     * Quando ocorre invalidação dos steps posteriores, as propriedades que armazenam os
     * dados precisam refletir os novos valores.
     */
    private updateStepsModel(cache: NmsWizardStepMapData): Promise<void> {
        this.endsModel = cache["EndsStepModel"] as EndsStepModel;
        this.intermediatesModel = cache["IntermediatesStepModel"] as IntermediatesStepModel;
        this.linksModel = cache["LinksStepModel"] as LinksStepModel;
        this.transpondersModel = cache["TranspondersStepModel"] as TranspondersStepModel;
        this.amplifiersModel = cache["AmplifiersStepModel"] as AmplifiersStepModel;
        this.model = cache["OscManagementStepModel"] as OscManagementStepModel;
        this.generalModel = cache["GeneralStepModel"] as GeneralStepModel;

        return Promise.resolve();
    }

    /**
     * Retorna os dados atuais.
     * Como a cache @see NmsWizardStepMapData é atualizada apenas no botão próximo do diagrama,
     * é preciso pegar os dados atuais para garantir o funcionamento correto do processChanges.
     */
    private getCurrentStepModel() {
        return {
            "EndsStepModel": this.endsModel,
            "TranspondersStepModel": this.transpondersModel,
            "IntermediatesStepModel": this.intermediatesModel,
            "LinksStepModel": this.linksModel,
            "AmplifiersStepModel": this.amplifiersModel,
            "OscManagementStepModel": this.model,
            "GeneralStepModel": this.generalModel
        };
    }

    resolveModel(): Promise<NmsWizardStepModel> {
        const configuredData = this.tableData.map((oscData: OscManagementData) => {
            const portData = oscData.ports.find(data => data.circuitPortIdentifierData.portNo.toString() === oscData.port)
            if (portData) {
                const oscPort: OscPort = {
                    identifier: oscData.identifier,
                    endId: oscData.endResourceId,
                    endName: oscData.endName,
                    oscChassisIdentifier: portData.circuitPortIdentifierData.chassisIdentifier,
                    oscSlotIdentifier: portData.circuitPortIdentifierData.slotIdentifier,
                    oscPortNo: portData.circuitPortIdentifierData.portNo,
                    oscPortModelIdentifier: portData.circuitPortIdentifierData.portModelIdentifier,
                    linkTxId: oscData.txLinkId,
                    linkRxId: oscData.rxLinkId,
                    oscPort: oscData.port,
                    toggleActive: oscData.toggleActive
                }

                return oscPort;
            }

            return null;

        }).filter(Boolean);

        this.model.oscManagementPorts = configuredData;
        this.model.allOscs = this.tableData;

        return Promise.resolve(this.model);
    }

    private buildOscInputData(endsStepModel: EndsStepModel, intermediatesStepModel: IntermediatesStepModel,
            linksStepModel: LinksStepModel) {
        const aEndId = endsStepModel.aEndDevice.id;
        const zEndId = endsStepModel.zEndDevice.id;
        const intermediatesResourceIds = intermediatesStepModel.devices.map(device => device.resourceId);
        const linksIds = linksStepModel.links.map(link => link.id);

        return new OscInputData(aEndId, zEndId, intermediatesResourceIds, linksIds);
    }
}
